"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { isAuthenticated, fetchWithAuth, UnauthorizedError } from "@/lib/auth";
import { isCeremonyActive } from "@/lib/ceremony-activity";
import {
  clearStripeDepositReturnStash,
  continueDepositByPaymentIntent,
  isDepositContinueTerminalHttpStatus,
  mergeStripeDepositReturnStash,
  OPEN_DEPOSITS_REFRESH_EVENT,
  parseStripeDepositReturnParams,
  readStripeDepositReturnStash,
  shouldRefreshWalletAfterWriteFailure,
  stripStripeDepositReturnParams,
} from "@/lib/open-deposits";
import { pollForCreditedBalance } from "@/lib/wallet-balance-poll";
import { parseWalletSummary, resolveWalletFrozen, frozenAfterWriteRejection, beginWalletRefresh, isCurrentWalletRefresh } from "@/lib/wallet-summary";
import { releaseStrandedDialogScrollLock } from "@/lib/release-dialog-scroll-lock";
import { AddFundsModal } from "@/components/wallet/add-funds-modal";

/** Avoid hammering continue-by-pi while polling for webhook credit. */
const CREDIT_STATUS_POLL_MIN_INTERVAL_MS = 3_000;

function dispatchOpenDepositsRefresh(): void {
  window.dispatchEvent(new CustomEvent(OPEN_DEPOSITS_REFRESH_EVENT));
}

function hasStripeDepositReturnParams(params: URLSearchParams): boolean {
  return (
    params.has("payment_intent") ||
    params.has("payment_intent_client_secret") ||
    params.has("redirect_status")
  );
}

export interface WalletContextValue {
  /** The one total (seeded + real) shown everywhere. */
  balanceCents: number | null;
  /** Real money that may be withdrawn, from the last refresh; null when unknown. */
  withdrawableCents: number | null;
  /** Max Bucks (pack-only credit) in cents; 0 when none or unknown. */
  maxBucksCents: number;
  frozen: boolean;
  loading: boolean;
  /**
   * The last refresh came back 401. The balance is null and the session is
   * gone server-side, but the page was NOT navigated away: a background read
   * (window focus, post-open reconcile) must never replace a reveal in
   * progress with the login page. The next explicit action that needs auth
   * redirects as before.
   */
  sessionStale: boolean;
  /** Resolves to the latest balance from the API, or null if unauthenticated / error. */
  refresh: () => Promise<number | null>;
  /** Max Bucks from the most recent refresh, readable right after `await refresh()`. */
  latestMaxBucksCents: () => number;
  bump: (deltaCents: number) => void;
  /** Apply a freeze 409 immediately, then refresh summary. */
  handleWriteFailure: (status?: number, errorCode?: string) => void;
  openAddFunds: (opts?: {
    suggestedCents?: number;
    onSuccess?: () => void;
    continueDeposit?: { clientSecret: string; amountCents: number };
  }) => void;
}

const WalletContext = createContext<WalletContextValue | null>(null);

export function WalletProvider({
  children,
  stripePublishableKey,
}: {
  children: ReactNode;
  stripePublishableKey: string;
}) {
  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  const [withdrawableCents, setWithdrawableCents] = useState<number | null>(null);
  const [maxBucksCents, setMaxBucksCents] = useState(0);
  const maxBucksCentsRef = useRef(0);
  const latestMaxBucksCents = useCallback(() => maxBucksCentsRef.current, []);
  const [frozen, setFrozen] = useState(false);
  const [sessionStale, setSessionStale] = useState(false);
  const balanceCentsRef = useRef<number | null>(null);
  const frozenRef = useRef(false);
  useEffect(() => {
    balanceCentsRef.current = balanceCents;
  }, [balanceCents]);
  useEffect(() => {
    frozenRef.current = frozen;
  }, [frozen]);
  const [loading, setLoading] = useState(true);
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [suggestedCents, setSuggestedCents] = useState<number | undefined>();
  const [continueDeposit, setContinueDeposit] = useState<{
    clientSecret: string;
    amountCents: number;
  } | null>(null);
  const [preConfirmBalanceCents, setPreConfirmBalanceCents] = useState<number | null>(null);
  const addFundsSuccessExtraRef = useRef<(() => void) | undefined>(undefined);
  const stripeResumeHandledRef = useRef(false);
  /** Snapshot taken when opening add-funds / before confirm — never from post-credit refresh. */
  const preConfirmBalanceRef = useRef<number | null>(null);
  const refreshGateRef = useRef({ latest: 0 });

  const refresh = useCallback(async (): Promise<number | null> => {
    if (typeof window === "undefined") return null;
    if (!isAuthenticated()) {
      setBalanceCents(null);
      frozenRef.current = false;
      setFrozen(false);
      setLoading(false);
      return null;
    }
    const gen = beginWalletRefresh(refreshGateRef.current);
    setLoading(true);
    try {
      // `throw` rather than the default redirect: refresh() is called from
      // background paths (focus, post-open reconcile) where a navigation
      // would tear down a ceremony. The 401 is handled in the catch below.
      const res = await fetchWithAuth("/api/v1/wallet", { onUnauthorized: "throw" });
      if (!isCurrentWalletRefresh(refreshGateRef.current, gen)) return null;
      setSessionStale(false);
      if (!res.ok) {
        // A transient failure (e.g. a 429 from a rapid burst of sellbacks)
        // must not blank the pill: leave the last known balance in place and
        // only null it out on an actual 401 (handled in the catch below).
        const nextFrozen = resolveWalletFrozen({
          previouslyFrozen: frozenRef.current,
          summary: null,
        });
        frozenRef.current = nextFrozen;
        setFrozen(nextFrozen);
        return null;
      }
      const json = (await res.json()) as { success?: boolean; data?: unknown };
      if (!isCurrentWalletRefresh(refreshGateRef.current, gen)) return null;
      const summary = json.success ? parseWalletSummary(json.data) : null;
      const nextFrozen = resolveWalletFrozen({
        previouslyFrozen: frozenRef.current,
        summary,
      });
      frozenRef.current = nextFrozen;
      setFrozen(nextFrozen);
      if (summary) {
        setBalanceCents(summary.balanceCents);
        setWithdrawableCents(summary.withdrawableCents ?? null);
        maxBucksCentsRef.current = summary.maxBucksCents;
        setMaxBucksCents(summary.maxBucksCents);
        return summary.balanceCents;
      }
      setBalanceCents(null);
      return null;
    } catch (err) {
      if (!isCurrentWalletRefresh(refreshGateRef.current, gen)) return null;
      if (err instanceof UnauthorizedError) {
        // Session gone: clear what we show, say so, and stay on the page.
        setBalanceCents(null);
        setSessionStale(true);
        frozenRef.current = false;
        setFrozen(false);
        return null;
      }
      // Network error / non-401 failure: keep the last known balance rather
      // than blanking the pill to $0 while retrying.
      const nextFrozen = resolveWalletFrozen({
        previouslyFrozen: frozenRef.current,
        summary: null,
      });
      frozenRef.current = nextFrozen;
      setFrozen(nextFrozen);
      return null;
    } finally {
      if (isCurrentWalletRefresh(refreshGateRef.current, gen)) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    function onFocus() {
      // A reveal is on screen: its own success/settle callbacks reconcile the
      // balance, and a refresh here would only toggle `loading` under the
      // overlay (or, before the 401 change, navigate away from it).
      if (isCeremonyActive()) return;
      void refresh();
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const bump = useCallback((deltaCents: number) => {
    setBalanceCents((c) => (c == null ? c : c + deltaCents));
  }, []);

  const handleWriteFailure = useCallback(
    (status?: number, errorCode?: string) => {
      if (frozenAfterWriteRejection(errorCode, frozenRef.current)) {
        frozenRef.current = true;
        setFrozen(true);
      }
      if (shouldRefreshWalletAfterWriteFailure(status, errorCode)) {
        void refresh();
      }
    },
    [refresh],
  );

  const makeCreditAppliedChecker = useCallback((paymentIntentId: string | null | undefined) => {
    if (!paymentIntentId) return undefined;
    let lastCheckAt = 0;
    let lastResult = false;
    return async (): Promise<boolean> => {
      if (lastResult) return true;
      const now = Date.now();
      if (now - lastCheckAt < CREDIT_STATUS_POLL_MIN_INTERVAL_MS) return lastResult;
      lastCheckAt = now;
      try {
        await continueDepositByPaymentIntent(fetchWithAuth, paymentIntentId);
        lastResult = false;
        return false;
      } catch (err) {
        const typed = err as Error & { httpStatus?: number; errorCode?: string };
        lastResult = isDepositContinueTerminalHttpStatus(typed.httpStatus, typed.errorCode);
        return lastResult;
      }
    };
  }, []);

  /**
   * Stripe `confirmPayment` / redirect return can resolve before the webhook credits
   * the wallet — poll using a pre-confirmation baseline (never a post-credit refresh).
   */
  const pollUntilCredited = useCallback(
    async (opts?: {
      amountCents?: number;
      baselineCents?: number | null;
      paymentIntentId?: string | null;
    }) => {
      const baseline =
        opts?.baselineCents !== undefined ? opts.baselineCents : preConfirmBalanceRef.current;
      await pollForCreditedBalance({
        baselineCents: baseline ?? null,
        refresh,
        amountCents: opts?.amountCents,
        isCreditApplied: makeCreditAppliedChecker(opts?.paymentIntentId),
      });
    },
    [refresh, makeCreditAppliedChecker],
  );

  /** Stripe `confirmPayment` can resolve before the webhook credits the wallet — poll until balance catches up. */
  const handleDepositSucceeded = useCallback(
    async (amountCents: number) => {
      const runExtra = addFundsSuccessExtraRef.current;
      addFundsSuccessExtraRef.current = undefined;
      if (amountCents <= 0) {
        await refresh();
        runExtra?.();
        return;
      }
      const stash = readStripeDepositReturnStash();
      await pollUntilCredited({
        amountCents,
        baselineCents: preConfirmBalanceRef.current ?? stash?.balanceCentsBefore ?? null,
        paymentIntentId: stash?.paymentIntentId ?? null,
      });
      clearStripeDepositReturnStash();
      runExtra?.();
    },
    [pollUntilCredited, refresh],
  );

  const openAddFunds = useCallback(
    (opts?: {
      suggestedCents?: number;
      onSuccess?: () => void;
      continueDeposit?: { clientSecret: string; amountCents: number };
    }) => {
      if (frozenRef.current) return;
      releaseStrandedDialogScrollLock();
      const snap = balanceCentsRef.current;
      preConfirmBalanceRef.current = snap;
      setPreConfirmBalanceCents(snap);
      addFundsSuccessExtraRef.current = opts?.onSuccess;
      setSuggestedCents(opts?.suggestedCents);
      setContinueDeposit(opts?.continueDeposit ?? null);
      setAddFundsOpen(true);
    },
    [],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const urlHasStripeParams = hasStripeDepositReturnParams(url.searchParams);

    let paymentIntentId: string | null = null;
    let redirectStatus: string | null = null;
    let baselineFromStash: number | null | undefined;
    let amountFromStash: number | null | undefined;

    if (urlHasStripeParams) {
      const parsed = parseStripeDepositReturnParams(url.searchParams);
      paymentIntentId = parsed.paymentIntentId;
      redirectStatus = parsed.redirectStatus;
      // Merge so pre-confirm balance written before redirect is preserved.
      mergeStripeDepositReturnStash({
        paymentIntentId,
        redirectStatus,
      });
      const stash = readStripeDepositReturnStash();
      baselineFromStash = stash?.balanceCentsBefore;
      amountFromStash = stash?.amountCents;

      const stripped = stripStripeDepositReturnParams(url);
      const nextUrl = `${stripped.pathname}${stripped.search}${stripped.hash}`;
      window.history.replaceState(null, "", nextUrl);
    } else {
      const stash = readStripeDepositReturnStash();
      if (!stash) return;
      paymentIntentId = stash.paymentIntentId;
      redirectStatus = stash.redirectStatus;
      baselineFromStash = stash.balanceCentsBefore;
      amountFromStash = stash.amountCents;
    }

    if (
      !paymentIntentId &&
      redirectStatus !== "succeeded" &&
      redirectStatus !== "processing"
    ) {
      clearStripeDepositReturnStash();
      return;
    }

    if (!isAuthenticated()) return;
    if (stripeResumeHandledRef.current) return;
    stripeResumeHandledRef.current = true;

    void (async () => {
      if (redirectStatus === "succeeded" || redirectStatus === "processing") {
        await pollUntilCredited({
          amountCents:
            typeof amountFromStash === "number" && amountFromStash > 0 ? amountFromStash : undefined,
          baselineCents: baselineFromStash ?? null,
          paymentIntentId,
        });
        dispatchOpenDepositsRefresh();
        clearStripeDepositReturnStash();
        return;
      }

      if (paymentIntentId) {
        try {
          const { clientSecret, amountCents } = await continueDepositByPaymentIntent(
            fetchWithAuth,
            paymentIntentId,
          );
          openAddFunds({
            continueDeposit: { clientSecret, amountCents },
            onSuccess: dispatchOpenDepositsRefresh,
          });
        } catch (err) {
          const typed = err as Error & { httpStatus?: number; errorCode?: string };
          handleWriteFailure(typed.httpStatus, typed.errorCode);
        } finally {
          // Always clear — network / invalid-body failures must not silently retry forever.
          clearStripeDepositReturnStash();
          dispatchOpenDepositsRefresh();
        }
        return;
      }

      dispatchOpenDepositsRefresh();
      clearStripeDepositReturnStash();
    })();
  }, [refresh, openAddFunds, pollUntilCredited, handleWriteFailure]);

  const handleAddFundsOpenChange = useCallback((open: boolean) => {
    setAddFundsOpen(open);
    if (!open) {
      addFundsSuccessExtraRef.current = undefined;
      setContinueDeposit(null);
      setSuggestedCents(undefined);
      setPreConfirmBalanceCents(null);
      // The user backed out without completing — don't leave a stale
      // paymentIntentId in the stash to auto-reopen this modal later.
      clearStripeDepositReturnStash();
    }
  }, []);

  useEffect(() => {
    if (frozen) handleAddFundsOpenChange(false);
  }, [frozen, handleAddFundsOpenChange]);

  const value = useMemo<WalletContextValue>(
    () => ({
      balanceCents,
      withdrawableCents,
      maxBucksCents,
      frozen,
      // Every consumer renders a placeholder while this is true, so it must
      // mean "nothing to show yet", not "a request is in flight". Background
      // refreshes (focus, the post-withdrawal poll) would otherwise flicker
      // the balance to an ellipsis every few seconds.
      loading: loading && balanceCents == null,
      sessionStale,
      refresh,
      latestMaxBucksCents,
      bump,
      handleWriteFailure,
      openAddFunds,
    }),
    [balanceCents, withdrawableCents, maxBucksCents, frozen, loading, sessionStale, refresh, latestMaxBucksCents, bump, handleWriteFailure, openAddFunds],
  );

  return (
    <WalletContext.Provider value={value}>
      {children}
      <AddFundsModal
        open={addFundsOpen}
        onOpenChange={handleAddFundsOpenChange}
        suggestedCents={suggestedCents}
        mode={continueDeposit ? "continue" : "create"}
        continueClientSecret={continueDeposit?.clientSecret ?? null}
        continueAmountCents={continueDeposit?.amountCents ?? null}
        preConfirmBalanceCents={preConfirmBalanceCents}
        stripePublishableKey={stripePublishableKey}
        onSuccess={({ amountCents }) => {
          void handleDepositSucceeded(amountCents);
        }}
        onWriteRejected={handleWriteFailure}
      />
    </WalletContext.Provider>
  );
}

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) {
    throw new Error("useWallet must be used within WalletProvider");
  }
  return ctx;
}

/** For components that may render outside the provider (should not happen in production). */
export function useWalletOptional(): WalletContextValue | null {
  return useContext(WalletContext);
}
